/** @jsxImportSource @emotion/react */
import { SerializedStyles } from "@emotion/react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { CloseIcon } from "components/Icons";
import { useGetMaxAmountToWrap } from "hooks/wrap/useGetMaxAmountToWrap";
import { FocusEventHandler, forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { default as CurrencyInputOriginal } from "react-currency-input-field";
import { withIsVisible } from "utils/reactUtils";
import { useBalanceForSymbol } from "../../../hooks/useBalanceForSymbol";
import { useCanQuote } from "../../../hooks/useCanQuote";
import { useGetTokenDecimals } from "../../../hooks/useGetTokenDecimals";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { Amount } from "../../../types/amount";
import {
  getDecimalSeparator,
  isZero,
  parseAmountForInput,
  parseInputToAmount,
} from "../../../utils/amountUtils";
import { isNativeToken } from "../../../utils/wrapUtils";
import { Text } from "../Text";
import { Hash } from "viem";

interface CurrencyInputProps {
  value: Amount | undefined;
  onValueChange?: (value: Amount, wasNumberPressed: boolean) => void;
  onBlurred?: (value: Amount | undefined) => void;
  symbol?: Hash;
  maxButton?: { symbol: Hash };
  clearButton?: boolean;
  classNames?: Record<string, boolean>;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  testId?: string;
  prefix?: string;
  style?: SerializedStyles;
  onFocus?: () => void;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      value,
      onValueChange,
      symbol,
      placeholder,
      disabled,
      id,
      testId,
      prefix,
      style,
      maxButton,
      clearButton = false,
      onBlurred,
      onFocus,
    },
    ref
  ) => {
    const theme = GlobalStore.useState(s => s.theme);
    const network = GlobalStore.useState(s => s.network);
    const decimalSeparator = useMemo(getDecimalSeparator, [getDecimalSeparator]);
    const [draftValue, setDraftValue] = useState<string>(parseAmountForInput(value));
    const canQuote = useCanQuote();
    const maxBalance = useBalanceForSymbol(maxButton?.symbol);
    const getTokenDecimals = useGetTokenDecimals();
    const getMaxAmountToWrap = useGetMaxAmountToWrap();

    // Rewrite this code  to arrange emulate this behaviour

    // Decoupling using events
    useEffect(() => {
      return () => {
        // Executed only when component unmounts,
        const e = new Event("componentUnmount");
        document.dispatchEvent(e);
      };
    }, []);

    useEffect(() => {
      function doOnUnmount() {
        if (draftValue && onBlurred) {
          onBlurred(parseInputToAmount(draftValue));
        }
      }

      document.addEventListener("componentUnmount", doOnUnmount);
      return () => {
        // This is done whenever value of somethingChanged changes
        document.removeEventListener("componentUnmount", doOnUnmount);
      };
    }, [draftValue, onBlurred]);

    const handleChange = (inputtedValue: string | undefined, wasNumberPressed: boolean) => {
      // Prevent user from typing if decimal places of token is exceeded
      const decimalPlaces = inputtedValue?.includes(decimalSeparator)
        ? inputtedValue.split(decimalSeparator)[1].length
        : undefined;
      if (symbol && decimalPlaces && decimalPlaces > getTokenDecimals(symbol)) return;

      try {
        if (inputtedValue === undefined) {
          setDraftValue("");
        } else {
          setDraftValue(inputtedValue);
        }
        const newAmount = parseInputToAmount(inputtedValue);
        if (newAmount == null) return;

        if (onValueChange != null) {
          onValueChange(newAmount, wasNumberPressed);
        }
      } catch (error) {
        console.error(`Cannot parse input amount: ${String(value)}`);
        Sentry.captureException(`Cannot parse input amount`, {
          extra: { e: error },
        });
      }
    };

    const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
      event => {
        if (isZero(draftValue)) {
          setDraftValue("");
        }
        event.target.select();
      },
      [draftValue]
    );

    const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(() => {
      const parsedValue = draftValue ? parseAmountForInput(parseInputToAmount(draftValue)) : "0.0";
      setDraftValue(parsedValue);
      onBlurred?.(draftValue ? parseInputToAmount(draftValue) : Amount.ZERO);
    }, [draftValue, onBlurred]);

    const handleMaxBalance = async () => {
      if (disabled) return;
      if (maxBalance && maxButton) {
        if (maxButton && isNativeToken(maxButton.symbol, network)) {
          const amountToWrap = await getMaxAmountToWrap(maxButton.symbol);
          if (amountToWrap !== null) {
            // Use wrap utils
            void handleChange(parseAmountForInput(amountToWrap), true);
          }
        } else {
          void handleChange(parseAmountForInput(maxBalance.balance), true);
        }
      }
      if (canQuote) {
        GlobalStore.update(s => {
          s.isRequestingQuote = true;
        });
      }
    };

    /* This is to account for when the user clicks balance
     *  Update draftValue if onValueChange & value changes as otherwise draftValue isn't aware we clicked the balance
     * */

    useEffect(() => {
      if (value === undefined || value?.value.eq(0)) {
        setDraftValue("");
      } else if (value) {
        setDraftValue(parseAmountForInput(value));
      }
    }, [onValueChange, value]);

    const MaxButton = withIsVisible(() => (
      <MaxButtonContainer onClick={() => void handleMaxBalance()}>
        <Text textStyle="Title" size="XS" as="span">
          Max
        </Text>
      </MaxButtonContainer>
    ));

    const ClearButton = withIsVisible(() => (
      <ClearButtonContainer
        onMouseDown={() => {
          handleChange("", false);
        }}
      >
        <CloseIcon width={22} height={22} color={theme.font.primary.default} />
      </ClearButtonContainer>
    ));
    return (
      <Container>
        <CurrencyInputBase
          ref={ref}
          intlConfig={{ locale: navigator.language }}
          decimalsLimit={36}
          allowNegativeValue={false}
          disableAbbreviations={true}
          disableGroupSeparators={true}
          decimalSeparator={decimalSeparator}
          value={draftValue}
          inputMode="decimal"
          autoComplete="off"
          autoCorrect="off"
          placeholder={placeholder ?? `0`}
          minLength={1}
          maxLength={79}
          spellCheck="false"
          onFocus={onFocus ?? handleFocus}
          onBlur={handleBlur}
          disabled={disabled ?? false}
          onValueChange={inputtedValue => {
            const wasNumberPressed = inputtedValue !== undefined;
            if (inputtedValue?.at(-1) === ".") {
              // Updates local state only
              setDraftValue(inputtedValue);
            } else {
              // Updates both local and GlobalStore
              handleChange(inputtedValue, wasNumberPressed);
            }
          }}
          transformRawValue={rawValue => {
            return rawValue === decimalSeparator ? `0${decimalSeparator}` : rawValue;
          }}
          prefix={prefix}
          id={id}
          data-testid={testId}
          css={style}
          name="amountInput"
        />{" "}
        <MaxButton isVisible={maxButton != null} />
        <ClearButton isVisible={clearButton && draftValue !== ""} />
      </Container>
    );
  }
);

// Sub-components

const CurrencyInputBase = styled(CurrencyInputOriginal)`
  :focus {
    outline: none;
  }

  ::placeholder {
    color: ${props => props.theme.font.secondary.default};
  }

  background: none;
  border: 0;
  text-align: right;
  color: ${props => props.theme.font.primary.default};
  font-size: 28px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  width: 100%;
`;

const Container = styled.div`
  position: relative;
`;

const MaxButtonContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 7px;
`;

const ClearButtonContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 14px;
  cursor: pointer;
`;
